// @mui material components
import Card from "@mui/material/Card";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDModal from "components/MDModal";

// Material Dashboard 2 PRO React examples
import DataTable from "examples/Tables/DataTable";
// Data
import CustomPageLayout from "examples/LayoutContainers/CustomPageLayout";
import { Button, Grid, IconButton, Tooltip } from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import { useCallback, useContext, useEffect, useState } from "react";
import { ModalContext } from "context/ModalCtx";

import MDButton from "components/MDButton";
import { useNavigate } from "react-router-dom";
import MDLink from "components/MDLink";
import { Add, Visibility } from "@mui/icons-material";
import { TABLE_FILTERS } from "utils/constants";
import {
  getFiltersFromCache,
  saveFiltersToCache,
  getSortByFromCache,
  saveSortByToCache,
} from "utils/cacheManager";
import { getTenants } from "lib/Api/Tenants";
import { getPlans } from "lib/Api/Plans";
import DefaultCell from "examples/Tables/components/DefaultCell";
import { STATUS_BUTTON_VALUE } from "utils/constants";
import moment from "moment";
import { updateTenantStatus } from "lib/Api/Tenants";
import { LoginFormContext } from "context/loginFormCtx";

function Tenants() {
  const { formIsEdit } = useContext(ModalContext);

  const { setNotification, showMessageNotification } = useContext(LoginFormContext);

  const navigate = useNavigate();

  const handleStatusChange = async (id, status) => {
    showMessageNotification({
      title: "Are you sure?",
      text: "You want to change the status of this tenant?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "var(--cc-confirm-button-color)",
      cancelButtonColor: "var(--cc-cancel-button-color)",
      confirmButtonText: "Yes, change it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        const response = await updateTenantStatus(id, { status: status ? 0 : 1 });

        if (response?.status === 200) {
          getTenantsData();
          setNotification({
            color: "success",
            icon: "check",
            title: "Success",
            content: response.data.message,
            open: true,
          });
        } else {
          setNotification({
            color: "error",
            icon: "check",
            title: "Error",
            content: response.data.message,
            open: true,
          });
        }
      }
    });
  };

  const dataTableData = {
    columns: [
      // {
      //   Header: "Agency Name",
      //   accessor: "agency_name",
      //   filterInputType: "text",
      //   sort: true,
      //   isVisible: false,
      //   Cell: ({ value }) => <DefaultCell value={value} />,
      // },
      {
        Header: "Company Name",
        accessor: "company_name",
        filterInputType: "text",
        sort: true,
        Cell: ({ value }) => <DefaultCell value={value} />,
      },
      {
        Header: "status",
        accessor: "status",
        filterInputType: "select",
        sort: true,
        filterOptions: [
          { label: "Active", value: 1 },
          { label: "Inactive", value: 0 },
        ],
        Cell: ({ value, row }) => (
          <MDButton
            color={STATUS_BUTTON_VALUE[value]?.color}
            sx={{
              background: `${STATUS_BUTTON_VALUE[value]?.background}`,
            }}
            size="small"
            circular={true}
            onClick={() => {
              handleStatusChange(row.original.id, value);
            }}
          >
            {STATUS_BUTTON_VALUE[value]?.label}
          </MDButton>
        ),
      },
      {
        Header: "Email",
        accessor: "email",
        filterInputType: "text",
        sort: true,
        Cell: ({ row }) => {
          const subscription = row.original.tenantUsers?.[0]?.user?.subscription;
          if (!subscription?.subscription_id) return "-";
          return <DefaultCell value={row.original.tenantUsers?.[0]?.user?.email || "-"} />;
        },
      },
      {
        Header: "Plan",
        accessor: "plan",
        filterInputType: "select",
        filterOptions: [],
        sort: true,
        Cell: ({ row }) => {
          const subscription = row.original.tenantUsers?.[0]?.user?.subscription;
          if (!subscription?.subscription_id) return "-";
          return <DefaultCell value={subscription?.plan?.name || "-"} />;
        },
      },
      {
        Header: "Plan Start Date",
        accessor: "plan_start_date",
        filterInputType: "date",
        sort: true,
        disableFuture: true,
        Cell: ({ row }) => {
          const subscription = row.original.tenantUsers?.[0]?.user?.subscription;
          if (!subscription?.subscription_id) return "-";
          const value = subscription?.purchase_date;
          return <DefaultCell value={value ? moment(value).format("MM/DD/YYYY") : "-"} />;
        },
      },
      {
        Header: "Plan End Date",
        accessor: "plan_end_date",
        filterInputType: "date",
        sort: true,
        Cell: ({ row }) => {
          const subscription = row.original.tenantUsers?.[0]?.user?.subscription;
          if (!subscription?.subscription_id) return "-";
          const value = subscription?.end_date;
          return <DefaultCell value={value ? moment(value).format("MM/DD/YYYY") : "-"} />;
        },
      },
      {
        Header: "Created At",
        accessor: "created_at",
        filterInputType: "date",
        sort: true,
        disableFuture: true,
        Cell: ({ value }) => <DefaultCell value={moment(value).format("MM/DD/YYYY")} />,
      },
      {
        Header: "Updated At",
        accessor: "updated_at",
        filterInputType: "date",
        sort: true,
        disableFuture: true,
        Cell: ({ value }) => <DefaultCell value={moment(value).format("MM/DD/YYYY")} />,
      },
      {
        Header: "Action",
        width: "3%",
        sort: false,
        Cell: ({ value, row }) => {
          return (
            <MDBox>
              <Tooltip title="View Tenant" placement="top">
                <IconButton
                  onClick={() => {
                    navigate(`/tenants/${row?.original?.id}`, { state: { isEditMode: false } });
                  }}
                >
                  <Visibility />
                </IconButton>
              </Tooltip>
            </MDBox>
          );
        },
      },
    ],

    rows: [],
  };

  const [isLoading, setIsLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [rowCount, setRowCount] = useState(0);
  const [tableData, setTableData] = useState({
    columns: dataTableData.columns,
    rows: [],
    count: 0,
  });

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState(getFiltersFromCache(TABLE_FILTERS.TENANTS) || []);
  const [sortBy, setSortBy] = useState(
    getSortByFromCache(TABLE_FILTERS.TENANTS) || [{ id: "company_name", desc: false }]
  );

  const handlePageChange = (page) => {
    setPage(page);
  };

  const handlePageSizeChange = (pageSize) => {
    setPageSize(pageSize);
    setPage(0);
  };

  const handleFiltersChange = (filterObject) => {
    setFilters(filterObject);
    saveFiltersToCache(TABLE_FILTERS.TENANTS, filterObject);
    setPage(0);
  };

  const handleClearAllFilters = () => {
    setFilters([]);
    saveFiltersToCache(TABLE_FILTERS.TENANTS, []);
    setPage(0);
  };

  const handleSortByChange = (sortObject) => {
    setSortBy(sortObject);
    saveSortByToCache(TABLE_FILTERS.TENANTS, sortObject);
  };

  const getTenantsData = async () => {
    try {
      setIsLoading(true);

      const sortId = sortBy[0]?.id;
      const sortVal = sortBy[0]?.desc ? "desc" : "asc";

      const updatedFilters = filters.map((filter) => {
        if (filter.id == "status" || (filter.id == "plan" && typeof filter.value === "object")) {
          return {
            ...filter,
            value: filter.value.value,
          };
        }
        return filter;
      });

      const filterBody = {
        page: page + 1,
        pageSize,
        filters: updatedFilters,
        sortBy: sortId ? [{ id: sortId, value: sortVal }] : [],
      };

      const response = await getTenants(filterBody);

      if (response && response.status === 200) {
        setTableData((previousTableData) => ({
          ...previousTableData,
          rows: response.data.data.tenants,
          count: response.data.data.totalTenants,
        }));
        setTotalPages(Math.ceil(response.data.data.totalTenants / pageSize));
        setRowCount(response?.data?.data?.totalTenants || 0);
      }
    } catch (error) {
      console.log("error (getTenantsData): ", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getPlanOptions = async () => {
    try {
      const response = await getPlans({
        sortBy: [{ id: "name", value: "asc" }],
        filters: [{ id: "plan_type", value: 1 }],
      });

      if (response?.status === 200) {
        const planOptions = response.data.data.rows.map((plan) => ({
          label: plan.name,
          value: plan.name,
        }));

        setTableData((previousTableData) => ({
          ...previousTableData,
          columns: previousTableData.columns.map((column) =>
            column.accessor === "plan" ? { ...column, filterOptions: planOptions } : column
          ),
        }));
      }
    } catch (error) {
      console.log("error (getPlanOptions): ", error);
    }
  };

  useEffect(() => {
    getPlanOptions();
  }, []);

  useEffect(() => {
    getTenantsData();
  }, [page, pageSize, filters, sortBy]);

  return (
    <CustomPageLayout>
      <MDBox pt={3} pb={3}>
        <Card>
          <MDBox p={3} lineHeight={1}>
            <Grid container justifyContent="space-between" spacing={3}>
              <Grid item>
                <MDTypography variant="h5" fontWeight="medium">
                  Manage Tenants
                </MDTypography>
                <MDTypography variant="button" color="text">
                  Here you can manage all the tenants.
                </MDTypography>
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => navigate("/tenants/register-tenant")}
                >
                  <Add />
                  <span style={{ marginLeft: "10px" }}>Register Tenant</span>
                </Button>
              </Grid>
            </Grid>
          </MDBox>

          <DataTable
            isLoading={isLoading}
            table={tableData}
            pageSize={pageSize}
            currentPage={page}
            totalPages={totalPages}
            handlePageChange={handlePageChange}
            handlePageSizeChange={handlePageSizeChange}
            filters={filters}
            handleFiltersChange={handleFiltersChange}
            sorting={sortBy}
            handleSortByChange={handleSortByChange}
            getData={getTenantsData}
            handleClearAllFilters={handleClearAllFilters}
            rowCount={rowCount}
          />
        </Card>
      </MDBox>
    </CustomPageLayout>
  );
}

export default Tenants;
